import Card from "@/components/ui/card";
import CardFallback from "@/components/ui/cardFallback";
import { useMetricsPermissions } from "../hooks/use-metrics-perms";
import { Icon } from "@elaniintc/goes-design-system";
import type { DashboardData } from "@/types/common/dashboard";

const Loading = () => {
  return (
    <Icon
      type="far"
      name="spinner-third"
      className="text-action-quiet animate-spin text-lg"
    />
  );
};

interface MetricsRowProps {
  isFetching: boolean;
  isLoading: boolean;
  data: DashboardData | undefined;
  refetch: () => void;
}

export const MetricsRow = ({
  isFetching,
  isLoading,
  data,
  refetch,
}: MetricsRowProps) => {
  const { canSeeMetricsRow, hasProcesses, hasFlows, hasProcedures } =
    useMetricsPermissions();

  if (!canSeeMetricsRow) return null;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between items-center">
        <h6
          data-testid="metrics-title"
          className="text-heading text-h6 font-semibold"
        >
          Métricas
        </h6>
        <button
          onClick={() => refetch()}
          className="text-action text-sm font-semibold cursor-pointer hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
          type="button"
          disabled={isFetching}
        >
          Actualizar
        </button>
      </div>
      <div
        data-testid="metrics-cards"
        className="grid grid-cols-1 tablet:grid-cols-3 gap-3"
      >
        {hasProcesses ? (
          <Card
            data-testid="processes-card"
            className="flex justify-between items-center gap-4"
          >
            <span className="text-body font-normal">Procesos</span>
            {isLoading ? (
              <Loading />
            ) : (
              <span className="text-heading font-semibold">
                {data?.data.metrics.processes_qty ?? "-"}
              </span>
            )}
          </Card>
        ) : (
          <CardFallback
            data-testid="processes-card-fallback"
            className="flex justify-center items-center gap-4 shadow-none"
          >
            <span className="text-center font-normal text-on-disabled-quiet">
              Sin permiso para ver estos datos
            </span>
          </CardFallback>
        )}
        {hasFlows ? (
          <Card
            data-testid="flows-card"
            className="flex justify-between items-center gap-4"
          >
            <span className="text-body font-normal">Flujos</span>
            {isLoading ? (
              <Loading />
            ) : (
              <span className="text-heading font-semibold">
                {data?.data.metrics.flows_qty ?? "-"}
              </span>
            )}
          </Card>
        ) : (
          <CardFallback
            data-testid="flows-card-fallback"
            className="flex justify-center items-center gap-4 text-center shadow-none"
          >
            <span className="text-center font-normal text-on-disabled-quiet">
              Sin permiso para ver estos datos
            </span>
          </CardFallback>
        )}
        {hasProcedures ? (
          <Card
            data-testid="formalities-card"
            className="flex justify-between items-center gap-4"
          >
            <span className="text-body font-normal">Trámites</span>
            {isLoading ? (
              <Loading />
            ) : (
              <span className="text-heading font-semibold">
                {data?.data.metrics.formality_requests_qty ?? "-"}
              </span>
            )}
          </Card>
        ) : (
          <CardFallback
            data-testid="formalities-card-fallback"
            className="flex justify-center items-center gap-4 shadow-none"
          >
            <span className="text-center font-normal text-on-disabled-quiet">
              Sin permiso para ver estos datos
            </span>
          </CardFallback>
        )}
      </div>
    </div>
  );
};
